import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  QueueStatusWidget,
  TimeRangeFilter,
  TokenStatusBadge,
  TokenActions,
  LocationsToggleFilters,
} from '@/components/ghl-dashboard'
import { LocationsGrandTotal } from '@/components/ghl-dashboard/LocationsGrandTotal'
import { LocationsSummaryTable } from '@/components/ghl-dashboard/LocationsSummaryTable'
import {
  useGHLLocationOverview,
  useGHLQueueStatus,
} from '@/hooks/useGHLDashboard'
import { useUrlTimeRange } from '@/hooks/useUrlTimeRange'
import {
  useUrlLocationFilters,
  applyLocationFilters,
} from '@/hooks/useUrlLocationFilters'
import { useTokenHealthMap } from '@/hooks/useTokenHealth'
import { formatStartDate, useNewSystemStartDates } from '@/hooks/useNewSystemStartDates'
import type { TokenStatus } from '@/hooks/useTokenHealth'
import {
  formatCost,
  calculateTotalCost,
  formatRelativeTime,
} from '@/lib/ghl-dashboard'
import { MapPin, BarChart3, CheckCircle, Clock, Cpu, ChevronRight, LayoutGrid, Table as TableIcon, FilterX, CalendarClock } from 'lucide-react'

type ViewMode = 'cards' | 'table'
const VIEW_MODE_STORAGE_KEY = 'ghl-locations-view-mode'

const MIDDLEWARE_URL = import.meta.env.VITE_GHL_MIDDLEWARE_URL || null

function LocationCardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="pt-6">
        <div className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function GHLLocationListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  // Time range is URL-backed: any change writes ?from&until&range_label
  // to the search string. Drilling into a per-location dashboard preserves
  // the search via the navigate calls below, so the filter follows the user.
  const [timeRange, setTimeRange] = useUrlTimeRange()

  // Helper: navigate to a location's dashboard while carrying current
  // search (which contains the time-range params we just set).
  function navigateToLocation(locationId: string) {
    navigate({
      pathname: `/admin/ghl-middleware/location/${locationId}`,
      search: location.search,
    })
  }

  // View toggle: cards vs table. Persisted to localStorage so the user's
  // preference survives reloads. Initializer function runs only on first
  // mount — avoids hitting localStorage on every render.
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'cards'
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'table' ? 'table' : 'cards'
  })

  function handleViewChange(mode: string) {
    const next: ViewMode = mode === 'table' ? 'table' : 'cards'
    setViewMode(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next)
    }
  }

  const overview = useGHLLocationOverview(timeRange)
  const queue = useGHLQueueStatus(MIDDLEWARE_URL)
  const tokenHealth = useTokenHealthMap()
  const startDates = useNewSystemStartDates()
  const [filters, setFilters] = useUrlLocationFilters()

  const allLocations = overview.data ?? []

  // Counts surfaced inside the filter selects so operators can see the size
  // of each cohort before committing to a filter. Computed from the full
  // unfiltered list so the numbers stay stable regardless of current filter.
  const filterCounts = useMemo(() => {
    if (allLocations.length === 0) return null
    let crmOn = 0
    for (const loc of allLocations) {
      if (loc.crm_automation_enabled) crmOn++
    }
    return {
      total: allLocations.length,
      crmOn,
      crmOff: allLocations.length - crmOn,
    }
  }, [allLocations])

  // Filter is applied once at the parent; both views (cards/table) and the
  // grand-total all consume the same filtered list. When operators choose
  // "Data Pipeline OFF only", the totals reflect that subset — intentional,
  // so they can answer "how much volume am I missing on locations with the
  // pipeline disabled?" without doing math in their head.
  const locations = useMemo(
    () => applyLocationFilters(allLocations, filters),
    [allLocations, filters]
  )

  const hasFilter = filters.crm !== 'all'
  const hasData = !overview.isLoading && locations.length > 0
  const isEmpty = !overview.isLoading && allLocations.length === 0
  const isFilteredEmpty =
    !overview.isLoading && allLocations.length > 0 && locations.length === 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">GHL Middleware</h1>
          <p className="text-sm text-muted-foreground">
            AI conversation analysis pipeline — select a location
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <LocationsToggleFilters
            filters={filters}
            onChange={setFilters}
            counts={filterCounts}
          />
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
        </div>
      </div>

      {/* Queue Status (persistent) */}
      <QueueStatusWidget
        status={queue.data}
        isLoading={queue.isLoading}
        isError={queue.isError}
      />

      {/* Grand Total — sums across the (filtered) location set. Hidden when
          filters reduce the set to zero so the totals don't display
          confusing all-zeros alongside the filter-empty banner. */}
      {!isFilteredEmpty && (
        <LocationsGrandTotal data={locations} isLoading={overview.isLoading} />
      )}

      {/* View toggle: Cards | Table */}
      {(hasData || overview.isLoading) && (
        <div className="flex items-center justify-end">
          <Tabs value={viewMode} onValueChange={handleViewChange}>
            <TabsList>
              <TabsTrigger value="cards" className="gap-1.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Cards
              </TabsTrigger>
              <TabsTrigger value="table" className="gap-1.5">
                <TableIcon className="h-3.5 w-3.5" />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Loading skeleton (cards view only — table has its own skeleton) */}
      {overview.isLoading && viewMode === 'cards' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <LocationCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty state (only when both are loaded with no data) */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <MapPin className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium">No locations found</p>
          <p className="text-sm mt-1">
            Locations appear here once the middleware processes its first webhook.
          </p>
        </div>
      )}

      {/* Filtered-to-zero state — separate copy + a clear-filters affordance
          so operators can recognize this is filter-driven, not data-empty. */}
      {isFilteredEmpty && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FilterX className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium">No locations match these filters</p>
          <p className="text-sm mt-1 mb-4">
            {filterCounts?.total ?? 0} locations are hidden by the current filter.
          </p>
          <button
            type="button"
            onClick={() => setFilters({ crm: 'all' })}
            className="text-sm font-medium text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Table view */}
      {viewMode === 'table' && !isEmpty && !isFilteredEmpty && (
        <LocationsSummaryTable
          data={locations}
          isLoading={overview.isLoading}
          onRowClick={navigateToLocation}
          startDates={startDates.data}
        />
      )}

      {/* Card grid view (existing layout, unchanged) */}
      {viewMode === 'cards' && hasData && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {locations.map((loc) => {
            const aiCost = calculateTotalCost(loc.total_input_tokens, loc.total_output_tokens)
            const locToken = tokenHealth.data?.locations[loc.location_id]
            const tokenStatus: TokenStatus = locToken?.status ?? 'unknown'
            // Account start date set: new opportunities go to Booking Sales Automation,
            // so the card is greyed out to show the account has been handled.
            const startDate = startDates.data?.get(loc.location_id) ?? null

            return (
              <Card
                key={loc.location_id}
                className={`cursor-pointer hover:border-primary/50 hover:shadow-md transition-all ${
                  startDate ? 'bg-muted text-muted-foreground' : ''
                }`}
                onClick={() => navigateToLocation(loc.location_id)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base truncate" title={loc.name}>
                        {loc.name}
                      </h3>
                      {loc.last_analysis_at && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last activity: {formatRelativeTime(loc.last_analysis_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                      <TokenStatusBadge status={tokenStatus} detail={locToken} size="sm" />
                      <TokenActions
                        locationId={loc.location_id}
                        tokenStatus={tokenStatus}
                        middlewareUrl={MIDDLEWARE_URL}
                        variant="icon-only"
                      />
                      <ChevronRight className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Analyses:</span>
                      <span className="font-medium">{loc.analysis_count.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Bookings:</span>
                      <span className="font-medium">{loc.bookings_detected}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Pending:</span>
                      <span className="font-medium">
                        {loc.pending_followups}
                        {loc.pending_followups > 0 && (
                          <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">
                            SMS
                          </Badge>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">AI Cost:</span>
                      <span className="font-medium">{formatCost(aiCost)}</span>
                    </div>
                  </div>

                  {loc.avg_confidence > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Avg confidence: {loc.avg_confidence}%</span>
                        <span>{loc.actions_executed} actions executed</span>
                      </div>
                    </div>
                  )}

                  {/*
                    Master-toggle badge (and the start date, when set). Green = enabled, gray = off.
                    CRM Automation gates stage moves, follow-up SMS, and
                    field writes. Color treatment matches the
                    LocationSettingsDialog so operators can mentally
                    connect the dashboard with the dialog.
                  */}
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant={loc.crm_automation_enabled ? 'default' : 'secondary'}
                      className="text-[10px] px-1.5 py-0"
                      title={
                        loc.crm_automation_enabled
                          ? 'CRM Automation ON — stage moves, status writes, and follow-up SMS fire normally'
                          : 'CRM Automation OFF — no stage moves, status writes, or follow-up SMS for this location'
                      }
                    >
                      CRM Automation {loc.crm_automation_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </div>

                  {startDate && (
                    <div
                      className="mt-3 flex items-center gap-1.5 text-xs text-foreground"
                      title="From this day on the middleware does no new work for this account; only follow-ups already scheduled keep sending."
                    >
                      <CalendarClock className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        Cut off: <span className="font-medium">{formatStartDate(startDate)}</span>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
